import * as React from 'react';
import { useState } from 'react';
import { PageContent } from 'client/ui/layout/page/content/PageContent';
import { CorePageLayout } from 'client/ui/layout/CorePageLayout';
import { RouteComponentProps } from 'react-router-dom';
import { PageHeading } from 'client/ui/layout/page/heading/PageHeading';
import { InvoiceBatchQueryDto } from 'common/dto/query/InvoiceBatchQueryDto';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { InvoiceBatchApi } from 'client/components/schema/invoice-batch/InvoiceBatchApi';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { PageHeadingTitle } from 'client/ui/layout/page/heading/PageHeadingTitle';
import { BreadcrumbsContainer } from 'client/ui/breadcrumb/BreadcrumbsContainer';
import { InvoiceBatchColumns } from 'client/components/schema/invoice-batch/table/InvoiceBatchColumns';
import { InvoiceBatchDto } from 'common/dto/generated/InvoiceBatchDto';
import { ColumnSortDirections } from 'client/ui/table/TableColumnSort';
import { SorterResult } from 'antd/lib/table/interface';
import { InvoiceBatchColumnSort } from 'common/dto/query/types/InvoiceBatchQueryTypes';
import { NetworkAlertPage } from 'client/components/errors/network-alert/NetworkAlertPage';
import { TableSticky } from 'client/ui/table/TableSticky';
import {
  getPaginationUrlQuery,
  setPaginationUrlQuery
} from 'client/components/tables/pagination/paginationUrlQuery';
import { getPaginationUrlColumns } from 'client/components/tables/pagination/paginationUrlColumns';
import { useEffect } from 'react';

interface Params {}

export interface BatchesListPageProps extends RouteComponentProps<Params> {}

export function BatchesListPage(props: BatchesListPageProps) {
  const [query, setQuery] = useState<InvoiceBatchQueryDto>(
    new InvoiceBatchQueryDto(
      getPaginationUrlQuery(props.location.search, {
        pageNumber: 1,
        pageSize: 20
      })
    )
  );

  const { response, loading, error } = useApiQuery(InvoiceBatchApi.list, {
    data: { query }
  });

  const batches = response?.data.items;

  const columns = getPaginationUrlColumns<
    InvoiceBatchDto,
    InvoiceBatchQueryDto
  >(InvoiceBatchColumns, query);

  useEffect(() => {
    setQuery(
      new InvoiceBatchQueryDto(
        getPaginationUrlQuery(props.location.search, {
          pageNumber: 1,
          pageSize: 20
        })
      )
    );
  }, [props.location.search]);

  useBreadcrumbItem({ path: '/', title: `Home` });
  useBreadcrumbItem({
    priority: 10,
    path: '/invoicing/invoices/all',
    title: `Fatture`
  });
  useBreadcrumbItem({
    priority: 20,
    path: props.location.pathname,
    title: `Lotti`,
    menuKey: 'invoicing',
    sideMenuKey: 'batches'
  });

  if (error) {
    return (
      <NetworkAlertPage
        message="Impossibile caricare l'elenco dei lotti"
        error={error}
      />
    );
  }

  return (
    <CorePageLayout>
      <PageHeading
        title={<PageHeadingTitle children="Elenco Lotti ricevuti da SDI" />}
        breadcrumbRender={() => <BreadcrumbsContainer />}
      />
      <PageContent noPadding noSpace>
        <TableSticky
          loading={loading}
          rowKey="id"
          columns={columns}
          dataSource={batches}
          size="middle"
          onRow={(record: InvoiceBatchDto) => ({
            style: {
              cursor: 'pointer'
            },
            onClick: () => {
              props.history.push(`/invoicing/batches/${record.id}`);
            }
          })}
          onChange={(pagination, filters, sorter) => {
            const { field, order } = sorter as SorterResult<InvoiceBatchDto>;

            const sortField = (field as unknown) as InvoiceBatchColumnSort;

            // Fornitore
            const supplierName = filters.supplierName
              ? filters.supplierName[0].toString()
              : null;
            const supplierFiscalNumber = filters.supplierFiscalNumber
              ? filters.supplierFiscalNumber[0].toString()
              : null;

            const batchQuery = new InvoiceBatchQueryDto({
              ...query,
              pageNumber: pagination.current ?? query.pageNumber,
              pageSize: pagination.pageSize ?? query.pageSize,
              orderBy: order
                ? InvoiceBatchColumnSort[sortField]
                : InvoiceBatchColumnSort.id,
              orderByDirection: order
                ? ColumnSortDirections[order]
                : ColumnSortDirections.descend,
              supplierFiscalNumber,
              supplierName
            });

            setPaginationUrlQuery(props, batchQuery);
          }}
          pagination={{
            size: 'small',
            position: ['bottomRight'],
            showSizeChanger: true,
            total: response?.data.meta.total,
            pageSize: query.pageSize,
            current: query.pageNumber,
            pageSizeOptions: ['5', '10', '20', '30', '40']
          }}
        />
      </PageContent>
    </CorePageLayout>
  );
}
